import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  Typography
} from '@mui/material';
import { toast } from 'react-toastify';
import apiClient from '../../shared/auth.interceptor';
import { errorToast } from '../../shared/toast.service';
import Loader from '../../shared/Loader/loader';

type Summary = 'off' | 'daily' | 'weekly';

export interface NotificationPreferences {
  failures: boolean;
  disconnects: boolean;
  upcomingFailures: boolean;
  successes: boolean;
  summary: Summary;
  timezone?: string;
}

type AlertKey = 'failures' | 'disconnects' | 'upcomingFailures' | 'successes';

const alerts: { key: AlertKey; label: string; help: string }[] = [
  { key: 'failures', label: 'Failed entries', help: 'Email me as soon as a scheduled entry fails.' },
  { key: 'disconnects', label: 'Disconnected clients', help: 'Email me when a client is disconnected from QuickBooks.' },
  {
    key: 'upcomingFailures',
    label: 'Entries that will fail',
    help: 'One email each morning listing entries due in the next 2 days for disconnected clients.'
  },
  { key: 'successes', label: 'Successful entries', help: 'Email me every time a scheduled entry posts.' }
];

export const notificationsEndpoint = '/v1/user/notification-preferences';

const browserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
};

const apiError = (error: any, fallback: string) => {
  const message = error?.response?.data?.message;
  return Array.isArray(message) ? message.join(', ') : message || fallback;
};

const Notifications = () => {
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [saved, setSaved] = useState<NotificationPreferences | null>(null);
  const [saving, setSaving] = useState(false);
  const [sendingPreview, setSendingPreview] = useState(false);

  useEffect(() => {
    apiClient
      .get<NotificationPreferences>(notificationsEndpoint)
      .then(({ data }) => {
        setPreferences(data);
        setSaved(data);
      })
      .catch((error) => errorToast(apiError(error, 'Could not load notification settings')));
  }, []);

  if (!preferences) return <Loader />;

  const changed = JSON.stringify(preferences) !== JSON.stringify(saved);
  const toggle = (key: AlertKey) => setPreferences({ ...preferences, [key]: !preferences[key] });

  const save = async () => {
    setSaving(true);
    try {
      // The browser timezone is saved for later use; every email goes out at 11:00 UTC for now.
      const { data } = await apiClient.put<NotificationPreferences>(notificationsEndpoint, {
        ...preferences,
        timezone: browserTimezone()
      });
      setPreferences(data);
      setSaved(data);
      toast.success('Notification settings saved');
    } catch (error) {
      errorToast(apiError(error, 'Could not save notification settings'));
    } finally {
      setSaving(false);
    }
  };

  const sendPreview = async () => {
    setSendingPreview(true);
    try {
      const { data } = await apiClient.post<{ sentTo: string }>(`${notificationsEndpoint}/preview`);
      toast.success(`Preview summary sent to ${data.sentTo}`);
    } catch (error) {
      errorToast(apiError(error, 'Could not send the preview summary'));
    } finally {
      setSendingPreview(false);
    }
  };

  return (
    <Box sx={{ overflowY: 'auto' }}>
      <Typography sx={{ mb: 3, fontSize: '1.5rem', fontWeight: 'bold' }}>Notifications</Typography>
      <Card sx={{ maxWidth: 720 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Summary email
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            One email covering every client you can see, grouped by client. Failures and disconnected clients are listed
            first, so the summary alone shows you everything. Sent at 11:00 UTC (early morning in the US); weekly
            summaries go out on Mondays.
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={preferences.summary}
              onChange={(event) => setPreferences({ ...preferences, summary: event.target.value as Summary })}
              inputProps={{ 'aria-label': 'Summary email frequency' }}
            >
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="off">Off</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Alerts
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Sent as they happen. If you turn an alert off, it still appears in your summary.
          </Typography>
          {alerts.map(({ key, label, help }) => (
            <Box key={key} sx={{ py: 1 }}>
              <FormControlLabel
                control={<Switch checked={preferences[key]} onChange={() => toggle(key)} />}
                label={<Typography sx={{ fontWeight: 500 }}>{label}</Typography>}
              />
              <Typography variant="body2" color="textSecondary" sx={{ ml: 6 }}>
                {help}
              </Typography>
            </Box>
          ))}

          <Box className="d-flex justify-content-between mt-4" sx={{ gap: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" color="secondary" onClick={sendPreview} disabled={sendingPreview}>
              {sendingPreview ? 'Sending…' : 'Send me a preview summary'}
            </Button>
            <Button variant="contained" onClick={save} disabled={!changed || saving} sx={{ minWidth: 150 }}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Notifications;
